import classNames from 'classnames'
import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import {
    getBankNameFromCardNumber,
    getShebaInfo,
    verifyCardNumber,
} from '@persian-tools/persian-tools'
import DotsLoading from 'components/loadings/dotsLoading'
import { toEnglishDigits } from 'utilities/helper/toEnglishNumber'
import useSetAddCard from 'pages/bankAccounts/add/hooks/useSetAddCard'
import useSetVerifiedCard from 'pages/bankAccounts/add/hooks/useSetVerifiedCard'

interface IProps {
    onAdded: (bankAccountId: number) => void
    onCancel: () => void
}

const group = (value: string) => value.match(/.{1,4}/g)?.join(' ') ?? value

/**
 * The add-account form, inside the payout account picker.
 *
 * The picker used to send the manager off to /manage-bank-cards/add. That
 * unmounted the loan / withdrawal / settlement they were halfway through,
 * and coming back (or a mini app reloading on the way) dropped them out of
 * the flow with their answers gone — «می‌پره بیرون». Adding the account
 * here keeps the flow mounted; the picker refetches and selects it.
 *
 * Same two calls as the page: create (the server runs the owner inquiry),
 * then confirm the holder's name the inquiry returned.
 */
export default function InlineAddAccount({ onAdded, onCancel }: IProps) {
    const [isIban, setIsIban] = useState(false)
    const [value, setValue] = useState('')
    const [isConfirming, setIsConfirming] = useState(false)
    const queryClient = useQueryClient()
    const { mutate, data, isLoading } = useSetAddCard(setIsConfirming)
    const { mutateVerify, isLoading: isVerifying } = useSetVerifiedCard({
        onVerified: (id) => {
            // the picker's list is this query; refetched, it carries the new
            // account and the picker selects it
            queryClient.invalidateQueries('init-payment-os-wallet-cards')
            onAdded(id)
        },
        source: 'payout_picker',
    })

    const hint = isIban
        ? getShebaInfo(`IR${value}`)?.persianName
        : getBankNameFromCardNumber(value)

    const submit = () => {
        posthog.capture('bank_account_add_submitted', {
            account_type: isIban ? 'iban' : 'card',
            source: 'payout_picker',
        })
        if (
            isIban
                ? getShebaInfo(`IR${value}`)
                : verifyCardNumber(Number(value))
        ) {
            mutate({ cardNumber: isIban ? `IR${value}` : value, isIban })
            return
        }
        toast.error('اطلاعات کارت وارد شده درست نیست')
    }

    if (isConfirming) {
        if (isLoading || !data) return <DotsLoading />
        return (
            <div className="rounded-lg bg-gray-bg p-3 my-2">
                <p className="text-center mb-2">
                    آیا اطلاعات زیر را تایید می‌کنید؟
                </p>
                <p className="text-center" dir="ltr">
                    {group(isIban ? data.iban : data.card_number)}
                </p>
                <p className="text-center mt-2 font-bold">{data.full_name}</p>
                <div className="flex gap-2 mt-3">
                    <button
                        type="button"
                        className="w-full bg-green-1 text-white rounded-md py-2"
                        disabled={isVerifying}
                        onClick={() => mutateVerify(data.id)}
                    >
                        {isVerifying ? '...' : 'تایید'}
                    </button>
                    <button
                        type="button"
                        className="w-full border-1 border-green-1 text-green-1 rounded-md py-2"
                        onClick={() => setIsConfirming(false)}
                    >
                        ویرایش
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="rounded-lg bg-gray-bg p-3 my-2">
            <div className="flex justify-center mb-3">
                <div className="flex bg-white rounded-full">
                    {[false, true].map((iban) => (
                        <button
                            type="button"
                            key={String(iban)}
                            className={classNames(
                                'px-4 py-1 rounded-full',
                                isIban === iban && 'bg-green-1 text-white'
                            )}
                            onClick={() => {
                                setIsIban(iban)
                                setValue('')
                            }}
                        >
                            {iban ? 'شبا' : 'کارت'}
                        </button>
                    ))}
                </div>
            </div>
            <div
                className="flex items-center bg-white rounded-md px-2"
                dir="ltr"
            >
                {isIban && <span className="text-gray-1">IR</span>}
                <input
                    className="w-full py-2 px-1 outline-none bg-transparent"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder={isIban ? '24 رقم شبا' : '16 رقم کارت'}
                    value={value}
                    maxLength={isIban ? 24 : 16}
                    onChange={(e) =>
                        setValue(
                            toEnglishDigits(e.target.value).replace(/\D/g, '')
                        )
                    }
                />
            </div>
            {hint && <p className="text-green-1 mt-1">{hint}</p>}
            <p className="text-xs text-gray-1 mt-2">
                حساب به نام خود شما در همیان ثبت می‌شود.
            </p>
            <div className="flex gap-2 mt-3">
                <button
                    type="button"
                    className="w-full bg-green-1 text-white rounded-md py-2"
                    disabled={isLoading || !value}
                    onClick={submit}
                >
                    {isLoading ? '...' : 'ثبت حساب'}
                </button>
                <button
                    type="button"
                    className="w-full border-1 border-green-1 text-green-1 rounded-md py-2"
                    onClick={onCancel}
                >
                    انصراف
                </button>
            </div>
        </div>
    )
}
